'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

interface RegisterFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dateOfBirth: string;
  password: string;
  confirmPassword: string;
  consentHealthData: boolean;
  consentTerms: boolean;
  consentMarketing: boolean;
}

interface RegisterFormProps {
  onSwitchToLogin: () => void;
}

export default function RegisterForm({ onSwitchToLogin }: RegisterFormProps) {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<RegisterFormData>();

  const password = watch('password');

  const onSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    // BACKEND INTEGRATION: POST /api/auth/register with full profile data
    // Should also: send email verification, create PatientProfile, log AuditLogEvent
    await new Promise((r) => setTimeout(r, 1400));
    toast.success(`Account created — welcome, ${data.firstName}!`, {
      description: 'Check your email to verify your address.',
    });
    setTimeout(() => router.push('/patient-intake-wizard'), 800);
    setIsLoading(false);
  };

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[hsl(var(--foreground))]">Create your account</h2>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
          Start your GLP-1 treatment journey — takes about 2 minutes
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* Name row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-1.5">
              First name
            </label>
            <input
              type="text"
              autoComplete="given-name"
              placeholder="Voornaam"
              className={`input-field ${errors.firstName ? 'input-error' : ''}`}
              {...register('firstName', { required: 'Required' })}
            />
            {errors.firstName && (
              <p className="mt-1 text-xs text-red-600">{errors.firstName.message}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-1.5">
              Last name
            </label>
            <input
              type="text"
              autoComplete="family-name"
              placeholder="Achternaam"
              className={`input-field ${errors.lastName ? 'input-error' : ''}`}
              {...register('lastName', { required: 'Required' })}
            />
            {errors.lastName && (
              <p className="mt-1 text-xs text-red-600">{errors.lastName.message}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-1.5">
            Email address
          </label>
          <input
            type="email"
            autoComplete="email"
            placeholder="uw@email.nl"
            className={`input-field ${errors.email ? 'input-error' : ''}`}
            {...register('email', {
              required: 'Email is required',
              pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email' },
            })}
          />
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>
          )}
        </div>

        {/* Phone */}
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-1.5">
            Mobile phone
          </label>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5">
            Used for 2FA and appointment reminders
          </p>
          <input
            type="tel"
            autoComplete="tel"
            placeholder="+31 6 12 345 678"
            className={`input-field ${errors.phone ? 'input-error' : ''}`}
            {...register('phone', {
              required: 'Phone number is required',
              pattern: { value: /^\+?[0-9\s\-()]{9,15}$/, message: 'Enter a valid Dutch phone number' },
            })}
          />
          {errors.phone && (
            <p className="mt-1.5 text-xs text-red-600">{errors.phone.message}</p>
          )}
        </div>

        {/* Date of birth */}
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-1.5">
            Date of birth
          </label>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mb-1.5">
            You must be 18 or older. Used for identity verification.
          </p>
          <input
            type="date"
            className={`input-field ${errors.dateOfBirth ? 'input-error' : ''}`}
            {...register('dateOfBirth', {
              required: 'Date of birth is required',
              validate: (v) => {
                const dob = new Date(v);
                const now = new Date();
                const age = now.getFullYear() - dob.getFullYear();
                return age >= 18 || 'You must be at least 18 years old';
              },
            })}
          />
          {errors.dateOfBirth && (
            <p className="mt-1.5 text-xs text-red-600">{errors.dateOfBirth.message}</p>
          )}
        </div>

        {/* Password */}
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-1.5">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Min. 8 characters"
              className={`input-field pr-11 ${errors.password ? 'input-error' : ''}`}
              {...register('password', {
                required: 'Password is required',
                minLength: { value: 8, message: 'Minimum 8 characters' },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Include uppercase, lowercase, and a number',
                },
              })}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-1.5 text-xs text-red-600">{errors.password.message}</p>
          )}
        </div>

        {/* Confirm password */}
        <div>
          <label className="block text-sm font-semibold text-[hsl(var(--foreground))] mb-1.5">
            Confirm password
          </label>
          <div className="relative">
            <input
              type={showConfirm ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder="Repeat your password"
              className={`input-field pr-11 ${errors.confirmPassword ? 'input-error' : ''}`}
              {...register('confirmPassword', {
                required: 'Please confirm your password',
                validate: (v) => v === password || 'Passwords do not match',
              })}
            />
            <button
              type="button"
              onClick={() => setShowConfirm((v) => !v)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors"
              aria-label={showConfirm ? 'Hide password' : 'Show password'}
            >
              {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {errors.confirmPassword && (
            <p className="mt-1.5 text-xs text-red-600">{errors.confirmPassword.message}</p>
          )}
        </div>

        {/* Consent checkboxes */}
        <div className="border border-[hsl(var(--border))] rounded-2xl p-4 space-y-3 bg-[#F8FAFB]">
          <p className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase tracking-wider mb-2">
            Consent & privacy (GDPR/AVG)
          </p>

          <div className="flex items-start gap-2.5">
            <input
              id="consentHealthData"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded border-gray-300 text-[#0F6B5E] focus:ring-[#0F6B5E] cursor-pointer"
              {...register('consentHealthData', { required: 'You must consent to health data processing to continue' })}
            />
            <label htmlFor="consentHealthData" className="text-xs text-[hsl(var(--foreground))] cursor-pointer leading-relaxed">
              <span className="font-semibold">Required:</span> I consent to Waell processing my health data (bijzondere persoonsgegevens) for medical assessment and treatment, and sharing it with the treating physician and partner pharmacy. <a href="#" className="text-[#0F6B5E] hover:underline">Read our privacy policy.</a>
            </label>
          </div>
          {errors.consentHealthData && (
            <div className="flex items-center gap-1.5 ml-6">
              <AlertCircle className="w-3 h-3 text-red-500" />
              <p className="text-xs text-red-600">{errors.consentHealthData.message}</p>
            </div>
          )}

          <div className="flex items-start gap-2.5">
            <input
              id="consentTerms"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded border-gray-300 text-[#0F6B5E] focus:ring-[#0F6B5E] cursor-pointer"
              {...register('consentTerms', { required: 'You must accept the terms to continue' })}
            />
            <label htmlFor="consentTerms" className="text-xs text-[hsl(var(--foreground))] cursor-pointer leading-relaxed">
              <span className="font-semibold">Required:</span> I accept the <a href="#" className="text-[#0F6B5E] hover:underline">Terms of Service</a> and confirm I am 18 years or older and residing in the Netherlands.
            </label>
          </div>
          {errors.consentTerms && (
            <div className="flex items-center gap-1.5 ml-6">
              <AlertCircle className="w-3 h-3 text-red-500" />
              <p className="text-xs text-red-600">{errors.consentTerms.message}</p>
            </div>
          )}

          <div className="flex items-start gap-2.5">
            <input
              id="consentMarketing"
              type="checkbox"
              className="w-4 h-4 mt-0.5 rounded border-gray-300 text-[#0F6B5E] focus:ring-[#0F6B5E] cursor-pointer"
              {...register('consentMarketing')}
            />
            <label htmlFor="consentMarketing" className="text-xs text-[hsl(var(--muted-foreground))] cursor-pointer leading-relaxed">
              Optional: I agree to receive educational content and program updates by email. Unsubscribe at any time.
            </label>
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="btn-primary w-full py-3 text-base"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Creating account…</span>
            </>
          ) : (
            'Create account & start intake'
          )}
        </button>
      </form>

      <p className="mt-5 text-center text-sm text-[hsl(var(--muted-foreground))]">
        Already have an account?{' '}
        <button
          onClick={onSwitchToLogin}
          className="text-[#0F6B5E] font-semibold hover:underline"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}